import { cn } from '@/lib/utils';
import type { Icon as PhosphorIcon } from '@phosphor-icons/react';
import { Spinner } from './Spinner';

// Brand-getönte Hover (kein generisches Grau) + `active:`-Press, das auf Touch
// (kein Hover!) das eigentliche Feedback liefert. Geometrie: Kristall-crisp (rounded-lg).
const variants = {
  primary: 'bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-800 shadow-soft border border-brand-600',
  secondary: 'bg-white text-slate-700 border border-slate-200 shadow-soft hover:bg-brand-50 hover:border-brand-200 hover:text-brand-700 active:bg-brand-100',
  ghost: 'text-slate-500 border border-transparent hover:bg-brand-50 hover:text-brand-700 active:bg-brand-100',
  danger: 'bg-rose-600 text-white hover:bg-rose-700 active:bg-rose-800 shadow-soft border border-rose-600',
  warning: 'bg-amber-500 text-white hover:bg-amber-600 active:bg-amber-700 shadow-soft border border-amber-500',
  success: 'bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800 shadow-soft border border-emerald-600',
} as const;

const sizes = {
  sm: 'px-3 py-1.5 text-xs rounded-lg gap-1.5',
  md: 'px-4 py-2 text-sm rounded-lg gap-2',
  lg: 'px-6 py-3 text-sm rounded-lg gap-2',
} as const;

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof variants;
  size?: keyof typeof sizes;
  loading?: boolean;
  icon?: PhosphorIcon;
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  icon: Icon,
  children,
  className,
  disabled,
  ...props
}: ButtonProps) {
  return (
    <button
      className={cn(
        'inline-flex items-center justify-center font-semibold transition-all focus-ring active:scale-[0.98]',
        variants[variant],
        sizes[size],
        (disabled || loading) && 'opacity-50 cursor-not-allowed active:scale-100',
        className,
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? <Spinner size="sm" /> : Icon ? <Icon size={size === 'sm' ? 14 : 16} /> : null}
      {children}
    </button>
  );
}
